import { useCallback } from 'react';
import * as Linking from 'expo-linking';

import { logger as LOG } from '@garnish/logger';

import type { OrderType } from '@sg/graphql-schema';

import { HandleRatingSubmission } from '../../RateOrderScreen.data';
import { useSaveFeedbackCase } from '../useSaveFeedbackCase';

import type { HostedFrameMessageEvent } from '@garnish/hosted-frame';

// ─────────────────────────────────────────────────────────────────────────────

export const useMomoSurveyMessages = (params: UseMomoSurveyMessagesParams) => {
  const { url, orderId, orderType, handleRatingSubmission } = params;
  const { saveFeedbackCase } = useSaveFeedbackCase();

  const onMessage = useCallback(
    async (event: HostedFrameMessageEvent) => {
      if (!isTrustedMomoOrigin(event, url)) return;

      const payload = parseMomoMessage(event.nativeEvent.data);
      const eventName = getMomosEventName(payload);

      switch (eventName) {
        case MomosEvent.SurveySubmit:
          if (!isSurveySubmitClick(payload)) break;

          await submitFeedback({
            saveFeedbackCase,
            orderId,
            orderType,
            handleRatingSubmission,
          });
          break;
        case MomosEvent.OpenReviewLink:
          if (!payload) break;

          handleUrlOpen(payload);
          break;
        default:
          break;
      }
    },
    [url, saveFeedbackCase, orderId, orderType, handleRatingSubmission],
  );

  return { onMessage };
};

// ─── Event Handlers ──────────────────────────────────────────────────────────

async function submitFeedback(deps: SubmitFeedbackDeps) {
  const { saveFeedbackCase, orderId, orderType, handleRatingSubmission } = deps;

  const isSuccessful = await saveFeedbackCase({
    orderId,
    orderType,
    isFeedbackComplete: true,
  });

  await handleRatingSubmission(orderId, isSuccessful);
}

function handleUrlOpen(payload: Record<string, unknown>) {
  const reviewUrl = extractGoogleReviewUrl(payload);
  if (!reviewUrl) return;

  void Linking.openURL(reviewUrl).catch((error: unknown) => {
    logger.error('Failed to open Google review URL', error);
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseMomoMessage(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'object' && data !== null) {
    return data as Record<string, unknown>;
  }

  if (typeof data !== 'string' || !data) return null;

  try {
    const parsed = JSON.parse(data) as unknown;

    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function getMomosEventName(payload: Record<string, unknown> | null): string | null {
  if (!payload) return null;
  return typeof payload.eventName === 'string' ? payload.eventName : null;
}

function isSurveySubmitClick(payload: Record<string, unknown> | null) {
  return payload?.eventType === MOMOS_CLICK_EVENT_TYPE;
}

function extractGoogleReviewUrl(payload: Record<string, unknown>): string | null {
  const candidate = payload.url;
  if (typeof candidate !== 'string' || !candidate) return null;

  return GOOGLE_REVIEW_URL_PATTERN.test(candidate) ? candidate : null;
}

function isTrustedMomoOrigin(event: HostedFrameMessageEvent, fallbackUrl: string) {
  const messageUrl =
    'url' in event.nativeEvent && typeof event.nativeEvent.url === 'string'
      ? event.nativeEvent.url
      : fallbackUrl;

  try {
    return MOMOS_TRUSTED_ORIGINS.has(new URL(messageUrl).origin);
  } catch {
    return true;
  }
}

// ─── Constants ───────────────────────────────────────────────────────────────

enum MomosEvent {
  SurveySubmit = 'Submit',
  OpenReviewLink = 'OpenReviewLink',
}

const MOMOS_CLICK_EVENT_TYPE = 'Click';

const MOMOS_TRUSTED_ORIGINS = new Set([
  'https://us-restaurant.momos.io',
  'https://us-web.momos.io',
]);

const GOOGLE_REVIEW_URL_PATTERN =
  /^https:\/\/(?:search\.google\.com|www\.google\.com|maps\.google\.com|maps\.app\.goo\.gl|g\.page)(?:[/?#]|$)/i;

// ─── Logger ──────────────────────────────────────────────────────────────────

const logger = LOG.extend('MomoSurvey');

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMomoSurveyMessagesParams = Readonly<{
  url: string;
  orderId: string;
  orderType: OrderType;
  handleRatingSubmission: HandleRatingSubmission;
}>;

type SubmitFeedbackDeps = Readonly<{
  saveFeedbackCase: ReturnType<typeof useSaveFeedbackCase>['saveFeedbackCase'];
  orderId: string;
  orderType: OrderType;
  handleRatingSubmission: HandleRatingSubmission;
}>;
